import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { workbenchRegister } from '../lib/auth.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import { PAGE_FORM } from '../lib/layout.js'
import { BTN_PRIMARY_BLOCK, INPUT_BLOCK } from '../lib/ui.js'


export default function RegisterPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const returnTo = params.get('return') || '/admin'

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async (e) => {
    e.preventDefault()
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await workbenchRegister({ email, password, firstName, lastName, company: company || undefined })
      // /v4/users/register emails a 6-char code, not a link. Send the user
      // to /verify to type it in. After they verify, the AS will accept the
      // /oauth2/authorize round-trip on sign-in.
      const qs = new URLSearchParams({ email })
      if (returnTo) qs.set('return', returnTo)
      navigate(`/verify?${qs.toString()}`)
    } catch (err) {
      setError(err.message || 'Registration failed.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className={`${PAGE_FORM} pb-12 pt-12`}>
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/60 sm:p-8">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Create an account</h1>
        <p className="mt-2 text-sm text-slate-600">
          One account works across Workbench, Bitol, Badge, and Next.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">First name</span>
              <input
                type="text"
                required
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={INPUT_BLOCK}
              />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Last name</span>
              <input
                type="text"
                required
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={INPUT_BLOCK}
              />
            </label>
          </div>

          <label className="block text-sm">
            <span className="block font-medium text-slate-700">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={INPUT_BLOCK}
            />
          </label>

          <label className="block text-sm">
            <span className="block font-medium text-slate-700">
              Company <span className="text-slate-400 font-normal">(optional)</span>
            </span>
            <input
              type="text"
              autoComplete="organization"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className={INPUT_BLOCK}
            />
          </label>

          <label className="block text-sm">
            <span className="block font-medium text-slate-700">Password</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={INPUT_BLOCK}
            />
          </label>

          <label className="block text-sm">
            <span className="block font-medium text-slate-700">Confirm password</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={INPUT_BLOCK}
            />
          </label>

          {error ? (
            <ErrorAlert title="Registration failed" message={error} source="RegisterPage" />
          ) : null}

          <button
            type="submit"
            disabled={loading || !email || !password || !firstName || !lastName}
            className={BTN_PRIMARY_BLOCK}
          >
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <div className="mt-4 text-center text-sm text-slate-600">
          Already have an account?{' '}
          <Link
            to={`/signin${returnTo ? `?return=${encodeURIComponent(returnTo)}` : ''}`}
            className="font-medium text-brand-700 hover:text-brand-800 hover:underline"
          >
            Sign in
          </Link>
        </div>

        <Link to="/" className="mt-6 block text-center text-sm text-slate-500 hover:text-slate-700">
          ← Back to calendar
        </Link>
      </div>
    </main>
  )
}
